<script setup lang="ts">
  import { registerRemotes } from '@module-federation/runtime'

  import { computed, ref, watch } from 'vue'
  import type { Ref } from 'vue'

  import { useRoute, useRouter } from 'vue-router'

  import { useTheme } from 'vuetify'

  import * as Sentry from '@sentry/vue'

  import { useFirestore } from 'vuefire'

  import { collection, doc, getDocFromServer, waitForPendingWrites } from 'firebase/firestore'

  import useOuraAuth from '#web-oura-tools/auth'

  import { updateRoutes } from '#ourahub-intra/router'

  import { useGleanChat } from '#ourahub-intra/composables/glean'
  import { useIntroTour } from '#ourahub-intra/composables/introTour'
  import { useServiceFavicon } from '#ourahub-intra/composables/serviceFavicon'

  import { useAppStore } from '#ourahub-intra/stores/application'
  import { useServicesStore } from '#ourahub-intra/stores/services'

  import { getAppPageTitle, getPathFromHash } from '#ourahub-intra/utilities'
  import { resolveFaviconColor } from '#ourahub-intra/utilities/favicon'

  import type { ServiceDetailsSection } from '#ourahub-intra/types/services'
  import type { AppEnvironment } from '#web-oura-tools/types/auth'

  const theme = useTheme()
  const route = useRoute()
  const router = useRouter()

  const ouraAuth = useOuraAuth()

  const firestore = useFirestore()

  const ouraHubStore = useAppStore()
  const servicesStore = useServicesStore()

  const activeService = computed(() => servicesStore.getService(route.name as string))

  watch(
    () => getAppPageTitle(activeService.value?.name),
    (title) => {
      document.title = title
    },
    { immediate: true },
  )

  const faviconColor = computed(() =>
    resolveFaviconColor(
      servicesStore.getCategory(ouraHubStore.activeService)?.color,
      theme.global.current.value.colors,
    ),
  )
  const faviconService = computed(() => servicesStore.getService(ouraHubStore.activeService))

  useServiceFavicon(faviconService, faviconColor)

  const { isEnabled: gleanEnabled } = useGleanChat()

  const { demoSettingsOpen, demoView, demoTypeMenuOpen, demoService, demoCategory, startTour } = useIntroTour()

  const envChanging = ref(false)
  const panelCollapsed = ref(false)
  const introChecked = ref(false)

  const serviceInfo: Ref<string | null> = ref(null)
  const serviceInfoSection = ref<ServiceDetailsSection>()

  function showServiceInfo(target?: { id?: string; section?: ServiceDetailsSection }) {
    serviceInfo.value = target?.id || (route.name as string) || null
    serviceInfoSection.value = target?.section
  }

  const env = import.meta.env.VITE_APP_ENV

  // Non-release builds can only be accessed by devs. For E2E tests, this check is bypassed
  const hasDevAccess = computed(
    () =>
      env === 'release' ||
      import.meta.env.VITE_BYPASS_DEV_CHECK === 'true' ||
      Boolean(ouraAuth.user.value?.hasPermission('Internal Tooling Team')),
  )

  // User and panel state change monitoring

  watch(ouraAuth.user, async (user) => {
    if (user) {
      Sentry.setUser({ email: user.email })

      // Change environment on initialization only
      if (router.getRoutes().length <= 1) {
        const cloudEnv = getPathFromHash(window.location.hash).split('/')[0] as AppEnvironment

        if (cloudEnv && ouraAuth.appEnv.value !== cloudEnv) {
          await changeEnvironment(cloudEnv)
        }
      }
    }
  })

  // The catalog always runs in the deployment env, launched apps switch to their own env
  watch([() => route.name, () => Boolean(ouraAuth.user.value)], async ([name, signedIn]) => {
    if (signedIn && name === 'home' && ouraAuth.appEnv.value !== env) {
      await changeEnvironment(env)
    }
  })

  watch(panelCollapsed, async (collapsedState) => {
    await ouraHubStore.updateAccountPrefs({ navRail: collapsedState })
  })

  // Register remotes and update routes when services change

  watch(
    () => servicesStore.servicesList,
    async (remoteServices) => {
      ouraHubStore.serviceCategories = {}

      ouraHubStore.serviceCategories = remoteServices.reduce(
        (categories, service) => {
          categories[service.id] = service.category

          return categories
        },
        {} as Record<string, string>,
      )

      const mfRemotes = registerServiceRemotes(remoteServices)

      updateRoutes(remoteServices)

      console.log('Updated services:', ouraAuth.appEnv.value)

      console.log('Registered remote urls:', mfRemotes)
      console.log('Registered route paths:', router.getRoutes())

      if (mfRemotes.length < 3) {
        console.warn(
          'Less than 3 remote modules registered:',
          JSON.stringify(remoteServices),
          JSON.stringify(mfRemotes),
        )
      }

      // Trigger route update to load the remote and check route
      await router.replace({ path: route.path })
    },
  )

  watch(
    () => ouraHubStore.accountPrefs?.theme,
    (themeName) => {
      if (themeName) {
        theme.change(themeName)
      }
    },
    { immediate: true },
  )

  watch(
    () => ouraHubStore.accountPrefs?.navRail,
    (navRail) => {
      if (navRail === true) {
        panelCollapsed.value = true
      }
    },
    { immediate: true },
  )

  // Global loading state to show spinner when something is loading

  const isLoading = computed(() => {
    return envChanging.value || ouraHubStore.isLoading || servicesStore.isLoading
  })

  // Conditionally auto-start the first-visit intro tour, only on the main page so it waits
  // until an app / tool opened by a deep link is closed. The route has no name until the
  // initial navigation resolves, so a deep link never looks like home.
  // Prefs must have loaded and the doc must exist, as the side bar the tour anchors to renders on the same condition
  watch(
    () => !isLoading.value && !!ouraHubStore.accountPrefs && !!ouraAuth.user.value?.id && route.name === 'home',
    async (ready) => {
      console.log(
        'Intro tour check',
        isLoading.value,
        ouraAuth.user.value?.id,
        JSON.stringify(ouraHubStore.accountPrefs),
      )

      if (!ready || introChecked.value) {
        return
      }

      introChecked.value = true

      const userId = ouraAuth.user.value?.id

      if (ouraHubStore.accountPrefs?.hasSeenIntro) {
        return
      }

      try {
        // Have to wait for pending writes, because otherwise might see "local" values returned from them
        await waitForPendingWrites(firestore)

        // Get data from server, so not accidentally shown intro twice
        const snapshot = await getDocFromServer(doc(collection(firestore, 'account-prefs'), userId))

        console.log('Intro server snapshot', JSON.stringify(snapshot.data()))

        if (!snapshot.data()?.hasSeenIntro) {
          await startTour()
        } else {
          console.warn('Intro already seen on server!')
        }
      } catch (error) {
        // Skip the tour on an inconclusive read
        console.log('Intro tour check failed', JSON.stringify(error))
      }
    },
    { immediate: true },
  )

  // Mounted and helper functions for the cloud environment handling

  function registerServiceRemotes(services: typeof servicesStore.servicesList) {
    const local = import.meta.env.VITE_OUTO_LIBS_URL.startsWith('http://localhost')

    const mfRemotes = services.flatMap((service) =>
      Object.entries(service.entry ?? {}).map(([entryKey, entry]) => ({
        type: 'module' as const,
        name: `${entryKey}-${service.id}`,
        entry: local ? 'http://localhost:5174/remote-entry.js' : `${entry}/remote-entry.js`,
      })),
    )

    registerRemotes(mfRemotes, { force: true })

    return mfRemotes
  }

  async function performLogout() {
    // Sign out first, so going home does not start an env switch for the signed-out user
    const logout = ouraAuth.logout()

    await router.push({ name: 'home', params: { env } })

    await logout
  }

  async function changeEnvironment(cloudEnv: AppEnvironment) {
    envChanging.value = true

    const currentEnv = ouraAuth.appEnv.value

    await ouraAuth.changeEnvironment(cloudEnv)

    if (cloudEnv !== currentEnv) {
      await router.push({ name: route.name, params: { env: ouraAuth.appEnv.value } })
    }

    envChanging.value = false
  }
</script>

<template>
  <v-app class="bg">
    <LogoSpinner v-if="ouraAuth.user.value === undefined" />

    <AuthView v-else-if="!ouraAuth.user.value" :user="ouraAuth.user.value" @login="ouraAuth.login()" />

    <template v-else-if="!hasDevAccess">
      <v-container class="d-flex" style="min-height: 100vh">
        <v-row>
          <v-col align="center" align-self="center" style="position: relative">
            <div>
              <h2 class="text-display-large text-center text-error">No Developer Access</h2>

              <p class="text-headline-large text-center text-white">
                This version of OuraHub is meant for testing by developers and not for normal users. Therefore only
                people in tooling team group have the access. If you need the access make a request in
                #help-oura-tooling channel. For normal use please use:
                <a class="text-info" href="https://apps.ourahub.com">https://apps.ourahub.com</a>
              </p>
            </div>
          </v-col>
        </v-row>
      </v-container>
    </template>

    <template v-else>
      <v-app-bar theme="dark" color="black" class="text-white bg-transparent">
        <template #prepend>
          <v-app-bar-nav-icon class="ml-2" @click="panelCollapsed = !panelCollapsed" />

          <v-avatar border size="large" class="logo mx-2" :class="ouraAuth.cloudEnv.value">
            <v-progress-linear color="white" :model-value="100" />
            <v-progress-circular width="4" color="white" :model-value="100" :indeterminate="isLoading" />
          </v-avatar>

          <v-app-bar-title class="font-weight-bold pl-1 mr-8">OuraHub Portal</v-app-bar-title>
        </template>

        <div class="d-flex flex-align-center v-toolbar-title font-weight-bold mr-8">
          <v-icon icon="mdi-minus" class="mr-2 ml-n10" />

          {{ activeService?.name ?? 'All apps and tools' }}

          <div
            v-if="activeService?.version"
            style="margin-top: 2px"
            class="text-blue-grey-lighten-4 font-weight-light text-lowercase ml-2 text-title-medium"
          >
            (v{{ activeService?.version }})
          </div>
        </div>

        <v-spacer />

        <div class="d-flex align-center" data-intro-tour-id="top-bar-actions">
          <OuraHubHelp @show-info="showServiceInfo($event)" />

          <OuraHubPrefs :loading="isLoading" :user="ouraAuth.user.value" />

          <OuraHubMenu :user="ouraAuth.user.value" />
        </div>
      </v-app-bar>

      <OuraHubPanel
        v-if="ouraHubStore.accountPrefs"
        :rail="panelCollapsed"
        :user="ouraAuth.user.value"
        @logout="performLogout()"
      />

      <v-main scrollable>
        <div v-if="isLoading" fluid class="d-flex flex-column flex-grow-1 align-center justify-center bg-background">
          <v-progress-circular
            indeterminate
            size="128"
            style="position: absolute; top: 50%; transform: translateY(-50%)"
          />
        </div>

        <div
          v-else
          data-intro-tour-id="catalog"
          style="min-height: 0; overflow: auto"
          class="d-flex flex-column flex-grow-1 flex-shrink-1 bg-background"
        >
          <router-view :key="ouraHubStore.accountPrefs?.theme" />
        </div>

        <OuraHubChat
          v-if="!isLoading && gleanEnabled && ouraHubStore.accountPrefs?.showChat !== false"
          :services="servicesStore.servicesList"
        />
      </v-main>

      <TwingateAlert />

      <SessionBanner :expires="ouraAuth.sessionExpiresAt.value" @login="ouraAuth.login()" />

      <UpdateBanner />
    </template>
  </v-app>

  <ServiceDetails
    v-if="!!serviceInfo"
    :id="serviceInfo"
    :focus-section="serviceInfoSection"
    @close="serviceInfo = null"
  />

  <ServiceDetails
    v-if="demoSettingsOpen"
    :view="demoView"
    :service="demoService"
    :category="demoCategory"
    :open-type-menu="demoTypeMenuOpen"
  />
</template>

<style scoped lang="scss">
  .bg {
    background: linear-gradient(to right bottom, #1c1b1a, #2f4a73);
  }

  .logo {
    display: flex;
    flex-direction: column;

    background-color: rgba(0, 0, 0, 0.5);
    padding: 12px;

    &.dev {
      border: 2px solid rgba(0, 0, 0, 0.5);
      background-color: rgba(140, 200, 140, 0.5);
    }

    &.stage {
      border: 2px solid rgba(0, 0, 0, 0.5);
      background-color: rgba(200, 120, 200, 0.5);
    }

    .v-progress-linear {
      margin-top: -3px;
      margin-bottom: 2px;
      max-width: 14px;
    }
  }

  :deep(.v-main__scroller) {
    display: flex;
    flex-direction: column;
  }
</style>
